<template>
  <v-container id="sign-up" class="d-flex flex-column" fluid>
    <v-container v-if="!type" style="max-width: 400px;">
      <v-layout row wrap align-center>
        <v-flex>
          <p class="font-weight-bold neutral--text pb-4 pt-6">Register as</p>
        </v-flex>
      </v-layout>
      <v-layout row wrap align-center>
        <v-flex>
          <v-btn
            depressed
            block
            large
            rounded
            color="neutral"
            @click="showTermsDialog('provider')"
            outlined
            class="font-weight-bold"
            >{{ $t('provider') }}
          </v-btn>
        </v-flex>
      </v-layout>
      <v-layout row wrap align-center class="pt-4">
        <v-flex>
          <v-btn
            depressed
            block
            large
            rounded
            color="neutral"
            @click="showTermsDialog('researcher')"
            outlined
            class="font-weight-bold"
            >{{ $t('researcher') }}
          </v-btn>
        </v-flex>
      </v-layout>
      <v-layout row wrap align-center class="pt-4">
        <v-flex>
          <v-btn
            depressed
            block
            large
            rounded
            color="neutral"
            @click="showTermsDialog('hostingRepresentative')"
            outlined
            class="font-weight-bold"
            style="max-width: 100%; white-space: normal; word-break: break-word; display: block; overflow-wrap: break-word;"
          >
            {{ $t('hosting-institution-representative') }}
          </v-btn>
        </v-flex>
      </v-layout>
      <v-layout row wrap align-center class="pt-10 pb-4">
        <v-flex>
          <p class="primary300--text font-weight-bold">
            {{ $t('has-account') }}
          </p>
        </v-flex>
      </v-layout>
      <v-layout row wrap align-center>
        <v-flex>
          <v-btn
            depressed
            block
            large
            rounded
            color="neutral"
            :disabled="loading"
            data-cy="login"
            @click="login()"
            class="white--text mb-6 font-weight-bold"
            >{{ $t('log-in') }}
          </v-btn>
        </v-flex>
      </v-layout>
    </v-container>

    <!-- Hosting Representative -->
    <v-container v-if="type === 'hostingRepresentative' && !verify">
      <v-layout row wrap align-center>
        <v-flex>
          <v-form
            v-model="valid"
            ref="hostingRepresentativeForm"
            :lazy-validation="false"
            class="auth-form text-left px-md-8 px-sm-2"
            fluid
          >
            <v-row class="justify-center">
              <v-col sm="12" class="pb-0">
                <v-alert
                  class="error"
                  id="error"
                  type="info"
                  dense
                  icon="mdi-information-outline"
                  v-if="errorSummary !== null"
                  >{{ errorSummary }}
                </v-alert>
              </v-col>
            </v-row>
            <v-row class="justify-center">
              <v-col cols="12" class="pb-0">
                <label class="font-weight-bold subtitle-2">{{ $t('email') }}</label>
                <v-text-field
                  v-model="user.email"
                  :placeholder="$t('email')"
                  type="email"
                  :rules="emailRules()"
                  outlined
                  class="pt-1"
                  data-cy="email"
                ></v-text-field>
              </v-col>
              <v-col cols="12" class="pb-0 pt-0">
                <label class="font-weight-bold subtitle-2">{{ $t('firstname') }}</label>
                <v-text-field
                  v-model.trim="user.firstname"
                  :placeholder="$t('firstname')"
                  type="text"
                  :rules="[...requiredRule(), ...minMaxLengthRule(4, 50)]"
                  outlined
                  class="pt-1"
                  data-cy="firstname"
                ></v-text-field>
              </v-col>
              <v-col cols="12" class="pb-0 pt-0">
                <label class="font-weight-bold subtitle-2">{{ $t('lastname') }}</label>
                <v-text-field
                  v-model.trim="user.lastname"
                  :placeholder="$t('lastname')"
                  type="text"
                  :rules="requiredRule()"
                  outlined
                  class="pt-1"
                  data-cy="lastname"
                ></v-text-field>
              </v-col>
              <v-col cols="12" class="pb-0 pt-0">
                <label class="font-weight-bold subtitle-2">{{ $t('password') }}</label>
                <v-text-field
                  v-model="user.password"
                  :placeholder="$t('password')"
                  type="password"
                  :rules="passwordRules()"
                  outlined
                  class="pt-1"
                  data-cy="password"
                ></v-text-field>
              </v-col>
              <v-col cols="12" class="pb-0 pt-0">
                <label class="font-weight-bold subtitle-2">{{ $t('password-confirm') }}</label>
                <v-text-field
                  v-model="user.confirmPassword"
                  :placeholder="$t('password-confirm')"
                  type="password"
                  outlined
                  class="pt-1"
                  :rules="confirmPasswordRules().concat(passwordConfirmationRule)"
                  data-cy="confirmPassword"
                ></v-text-field>
              </v-col>
              <v-col cols="12" class="pb-0 pt-0">
                <label class="font-weight-bold subtitle-2">{{ $t('phone') }}</label>
                <v-text-field
                  v-model="user.phone"
                  :placeholder="$t('phone')"
                  type="text"
                  :rules="requiredRule()"
                  outlined
                  class="pt-1"
                  data-cy="provider"
                ></v-text-field>
              </v-col>
              <v-col cols="12" class="pb-0 pt-0">
                <label class="font-weight-bold subtitle-2">{{ $t('affiliation') }}</label>
                <v-text-field
                  v-model="user.affiliation"
                  :placeholder="$t('position')"
                  type="text"
                  :rules="requiredRule()"
                  outlined
                  class="pt-1"
                  data-cy="provider"
                ></v-text-field>
              </v-col>
              <v-col cols="12" class="pb-0 pt-0" v-if="user.memberOfExistingHostingInstitution">
                <label class="font-weight-bold subtitle-2">{{ $t('institution') }}</label>
                <v-autocomplete
                  v-model="user.institution"
                  :items="institutions"
                  :placeholder="$t('institution')"
                  :item-text="'name'"
                  outlined
                  return-object
                  :rules="requiredRule()"
                  class="required"
                >
                </v-autocomplete>
              </v-col>
              <v-col v-if="user.createHostingInstitution" cols="12" class="pb-0 pt-0">
                <label class="font-weight-bold subtitle-2">{{ $t('applicantInstitution') }}</label>
                <v-text-field
                  v-model="user.institutionText"
                  :placeholder="$t('applicantInstitution')"
                  type="text"
                  :rules="requiredRule()"
                  outlined
                  class="pt-1"
                ></v-text-field>
              </v-col>
              <v-col cols="12" class="pb-0 pt-0">
                <v-checkbox
                  :label="
                    'I assert that I am an authorized representative of a Hosting Institution.'
                  "
                  value="accept"
                  v-model="user.accept"
                  :rules="requiredRule()"
                  color="secondary"
                ></v-checkbox>
              </v-col>
              <v-col cols="12" class="pb-0 pt-0" v-if="!user.memberOfExistingHostingInstitution">
                <v-checkbox
                  :label="
                    'Register for creating a Hosting Institution in the Catalogue & Marketplace.'
                  "
                  value="accept"
                  v-model="user.createHostingInstitution"
                  :rules="requiredRule()"
                  color="secondary"
                ></v-checkbox>
              </v-col>
              <v-col cols="12" class="pb-0 pt-0" v-if="!user.createHostingInstitution">
                <v-checkbox
                  :label="
                    'Register to become a member/administrator of an existing Hosting Institution.'
                  "
                  value="accept"
                  v-model="user.memberOfExistingHostingInstitution"
                  :rules="requiredRule()"
                  color="secondary"
                ></v-checkbox>
              </v-col>
              <v-col cols="12" class="pb-0 pt-0">
                <v-btn
                  color="neutral"
                  :disabled="loading || !valid"
                  data-cy="login"
                  depressed
                  block
                  large
                  rounded
                  @click="submitHostingRepresentative"
                  class="white--text mb-6 mt-10 font-weight-bold"
                  >{{ $t('sign-up') }}
                </v-btn>
              </v-col>
            </v-row>
          </v-form>
        </v-flex>
      </v-layout>
    </v-container>

    <!-- Provider and Researcher -->
    <v-container
      v-if="
        (type === 'provider' || type === 'researcher') &&
          type !== 'hostingRepresentative' &&
          !verify
      "
    >
      <v-layout row wrap align-center>
        <v-flex>
          <v-form
            v-model="valid"
            ref="form"
            :lazy-validation="false"
            class="auth-form text-left px-md-8 px-sm-2"
            @click="submit"
            fluid
          >
            <v-row class="justify-center">
              <v-col sm="12" class="pb-0">
                <v-alert
                  class="error"
                  id="error"
                  type="info"
                  dense
                  icon="mdi-information-outline"
                  v-if="errorSummary !== null"
                  >{{ errorSummary }}
                </v-alert>
              </v-col>
            </v-row>
            <v-row class="justify-center">
              <v-col cols="12" class="pb-0">
                <label class="font-weight-bold subtitle-2">{{ $t('email') }}</label>
                <v-text-field
                  v-model="user.email"
                  :placeholder="$t('email')"
                  type="email"
                  :rules="emailRules()"
                  outlined
                  class="pt-1"
                  data-cy="email"
                ></v-text-field>
              </v-col>
              <v-col cols="12" class="pb-0 pt-0">
                <label class="font-weight-bold subtitle-2">{{ $t('firstname') }}</label>
                <v-text-field
                  v-model.trim="user.firstname"
                  :placeholder="$t('firstname')"
                  type="text"
                  :rules="[...requiredRule(), ...minMaxLengthRule(4, 50)]"
                  outlined
                  class="pt-1"
                  data-cy="firstname"
                ></v-text-field>
              </v-col>
              <v-col cols="12" class="pb-0 pt-0">
                <label class="font-weight-bold subtitle-2">{{ $t('lastname') }}</label>
                <v-text-field
                  v-model.trim="user.lastname"
                  :placeholder="$t('lastname')"
                  type="text"
                  :rules="requiredRule()"
                  outlined
                  class="pt-1"
                  data-cy="lastname"
                ></v-text-field>
              </v-col>
              <v-col cols="12" class="pb-0 pt-0">
                <label class="font-weight-bold subtitle-2">{{ $t('password') }}</label>
                <v-text-field
                  v-model="user.password"
                  :placeholder="$t('password')"
                  type="password"
                  :rules="passwordRules()"
                  outlined
                  class="pt-1"
                  data-cy="password"
                ></v-text-field>
              </v-col>
              <v-col cols="12" class="pb-0 pt-0">
                <label class="font-weight-bold subtitle-2">{{ $t('password-confirm') }}</label>
                <v-text-field
                  v-model="user.confirmPassword"
                  :placeholder="$t('password-confirm')"
                  type="password"
                  outlined
                  class="pt-1"
                  :rules="confirmPasswordRules().concat(passwordConfirmationRule)"
                  data-cy="confirmPassword"
                ></v-text-field>
              </v-col>
              <v-col cols="12" class="pb-0 pt-0" v-if="type == 'provider'">
                <label class="font-weight-bold subtitle-2">{{ $t('phone') }}</label>
                <v-text-field
                  v-model="user.phone"
                  :placeholder="$t('phone')"
                  type="text"
                  :rules="requiredRule()"
                  outlined
                  class="pt-1"
                  data-cy="provider"
                ></v-text-field>
              </v-col>
              <v-col cols="12" class="pb-0 pt-0" v-if="type == 'provider'">
                <label class="font-weight-bold subtitle-2">{{ $t('affiliation') }}</label>
                <v-text-field
                  v-model="user.affiliation"
                  :placeholder="$t('position')"
                  type="text"
                  :rules="requiredRule()"
                  outlined
                  class="pt-1"
                  data-cy="provider"
                ></v-text-field>
              </v-col>
              <v-col cols="12" class="pb-0 pt-0" v-if="type == 'provider'">
                <label class="font-weight-bold subtitle-2">{{ $t('institution') }}</label>
                <v-autocomplete
                  v-model="user.institution"
                  :items="institutions"
                  :placeholder="$t('institution')"
                  :item-text="'name'"
                  outlined
                  return-object
                  :rules="requiredRule()"
                  class="required"
                >
                </v-autocomplete>
              </v-col>
              <v-col cols="12" class="pb-0 pt-0" v-else>
                <label class="font-weight-bold subtitle-2"
                  >{{ $t('institution') }} <span class="font-weight-bold neutral500--text"></span
                ></label>
                <v-autocomplete
                  v-if="!isNotListedHostingInstitution"
                  v-model.trim="user.institutionText"
                  :items="researcherInstitutions"
                  :placeholder="$t('institution')"
                  item-value="name"
                  outlined
                  return-object
                  clearable
                  :rules="requiredRule()"
                  no-data-text="Press enter button to add a new institution."
                  class="required"
                  @keyup.enter="addInstitutionTag"
                  @update:search-input="typedInstitution = $event"
                />
                <v-text-field
                  v-if="isNotListedHostingInstitution"
                  v-model.trim="user.institutionText"
                  :placeholder="'Type the hosting institution you want, which is not listed.'"
                  type="text"
                  outlined
                  :rules="requiredRule()"
                  class="required"
                  clearable
                  @click:clear="clearInstitutionText"
                />
              </v-col>
              <v-col cols="12" class="pb-0 pt-0">
                <v-checkbox
                  v-if="type == 'provider'"
                  :label="$t('accept-terms')"
                  value="accept"
                  v-model="user.accept"
                  :rules="requiredRule()"
                  color="secondary"
                ></v-checkbox>
              </v-col>
              <v-col cols="12" class="pb-0 pt-0">
                <v-btn
                  color="neutral"
                  :disabled="loading || !valid"
                  data-cy="login"
                  depressed
                  block
                  large
                  rounded
                  @click="submit"
                  class="white--text mb-6 mt-10 font-weight-bold"
                  >{{ $t('sign-up') }}
                </v-btn>
              </v-col>
            </v-row>
          </v-form>
        </v-flex>
      </v-layout>
    </v-container>

    <!-- Verification -->
    <v-container v-if="verify">
      <img
        id="verify-img"
        alt="Verify-logo"
        class="rounded-0 text-center"
        :src="require('@/assets/svg/verify-email.svg')"
      />
      <h3 class="neutral600--text">{{ $t('verify-email') }}</h3>
      <p class="neutral600--text pt-5">{{ $t('verify-email-message') }}</p>
      <!-- <p
        v-if="user.createHostingInstitution || user.memberOfExistingHostingInstitution"
        class="neutral600--text pt-5"
      >
        When we <span class="text-bold">evaluate</span> your registration, you will be able to
        <span class="text-bold">log in</span> and <br /><span class="text-bold">add</span> your
        hosting institution data.
      </p> -->
      <v-btn
        color="primary200"
        :disabled="loading"
        data-cy="login"
        large
        :to="'/'"
        class="rounded-lg white--text mb-6 mt-5 pl-16 pr-16"
        >Home
      </v-btn>
    </v-container>
    <!--Accept Terms Modal-->
    <v-dialog v-model="acceptTerms" max-width="800">
      <v-card class="pa-8 rounded-xl secondaryBackground">
        <v-card-title class="pb-10">
          <v-row>
            <v-col cols="12" class="justify-center d-flex">
              <router-link :to="'/'">
                <img
                  alt="Logo"
                  width="180"
                  class="rounded-0 text-center"
                  :src="require('@/assets/svg/logo.svg')"
                />
              </router-link>
            </v-col>
          </v-row>
          <v-btn icon @click="acceptTerms = false" left>
            <v-icon>mdi-close</v-icon>
          </v-btn>
        </v-card-title>
        <v-divider class="pb-10"></v-divider>
        <v-card-text>
          <v-layout row wrap align-center>
            <v-flex>
              <v-form ref="form" :lazy-validation="true" @keyup.native.enter="submit" fluid>
                <v-row class="justify-center">
                  <v-col cols="12" class="mb-6 pb-0">
                    <p>
                      We respect your concerns about privacy of the data that we collect and process
                      for the purposes of maintaining this portal. The detailed information as to
                      how we treat your data is described in our
                      <router-link :to="{ name: 'PrivacyPolicy' }">Privacy Policy</router-link>.
                    </p>
                    <p>
                      By registration or continuing to browse this portal, you agree with the terms
                      and conditions described in our Privacy Policy and our
                      <router-link :to="{ name: 'PrivacyPolicy' }">Terms and Conditions</router-link
                      >.
                    </p>
                    <p>
                      If any data you have provided to us becomes inaccurate or out of date, or if
                      you have any queries about us processing your personal data, please contact
                      the ATRC’s Data Privacy Officer using the following contact details:
                      info{at}researchhub.ae, or +971 2 546 2022.
                    </p>
                  </v-col>
                  <v-col cols="6" class="d-flex justify-end pb-10">
                    <v-btn
                      color="neutral"
                      @click="decline()"
                      :disabled="loading"
                      data-cy="login"
                      large
                      block
                      rounded
                      outlined
                      depressed
                      class=" white--text font-weight-bold"
                      >{{ $t('decline') }}
                    </v-btn>
                  </v-col>
                  <v-col cols="6" class="pb-10">
                    <v-btn
                      color="primary200"
                      @click="accept()"
                      :disabled="loading"
                      data-cy="login"
                      large
                      block
                      rounded
                      depressed
                      class="white--text font-weight-bold"
                      >{{ $t('accept') }}
                    </v-btn>
                  </v-col>
                </v-row>
              </v-form>
            </v-flex>
          </v-layout>
        </v-card-text>
      </v-card>
    </v-dialog>
  </v-container>
</template>

<script>
import util from '@/mixins/util.js';
import EventBus from '@/helpers/event-bus.js';
import i18n from '../../i18n.js';
import service from '@/services/base-service';
import Vue from 'vue';

export default {
  name: 'SignUp',
  mixins: [util, service],
  data: () => ({
    loading: false,
    valid: false,
    type: null,
    verify: false,
    acceptTerms: false,
    user: {
      email: null,
      firstname: null,
      lastname: null,
      password: null,
      confirmPassword: null,
      accept: null,
      institution: null,
      affiliation: null,
      institutionText: null,
      createHostingInstitution: null,
      memberOfExistingHostingInstitution: null,
    },
    institutions: [],
    researcherInstitutions: [],
    responseStatus: null,
    errorSummary: null,
    isNotListedHostingInstitution: false,
    typedInstitution: '',
  }),
  created() {
    console.log(this.$route.params.applyForm);
  },
  mounted() {
    this.getInstitutions().then(res => {
      this.institutions = res.data;
    });

    this.axios.get('/api/v1/hosting-institutions/researcher/by-name?name=').then(res => {
      this.researcherInstitutions = res.data.content;
    });
  },
  methods: {
    addInstitutionTag() {
      const selectedInstitution = this.user.institutionText || this.typedInstitution?.trim();
      console.log('Typed Institution:', selectedInstitution);

      const exists = this.researcherInstitutions.some(inst => inst.name === selectedInstitution);

      if (!exists && selectedInstitution) {
        this.user.institutionText = selectedInstitution;
        this.isNotListedHostingInstitution = true;
      }
    },
    clearInstitutionText() {
      this.isNotListedHostingInstitution = false;
      this.user.institutionText = null;
    },
    decline() {
      this.type = null;
      this.acceptTerms = false;
    },
    accept() {
      if (this.selectedType) {
        this.type = this.selectedType;
        this.acceptTerms = false;
      }
    },
    showTermsDialog(selectedType) {
      this.selectedType = selectedType;
      this.acceptTerms = true;
    },
    submit() {
      if (this.type == 'provider' && !this.user.accept) {
        EventBus.$emit('snackbar', {
          text: 'You must accept the terms',
          color: 'error',
        });
        return;
      }
      console.log('[LogIn] Trying to sign up...');
      this.loading = true;
      EventBus.$emit('waiting', true);
      let url =
        this.type == 'provider'
          ? '/api/v1/auth/sign-up-provider'
          : '/api/v1/auth/sign-up-researcher';
      this.axios
        .post(url, this.user)
        .then(() => {
          console.log('Success');
          this.verify = true;
        })
        .catch(error => {
          console.log(error);
          this.errorSummary = error.response.data;
          if (error.response && error.response.status === 409) {
            EventBus.$emit('snackbar', {
              text: error.response.data,
              color: 'error',
            });
          }
        })
        .finally(() => {
          this.loading = false;
          EventBus.$emit('waiting', false);
        });
    },
    submitHostingRepresentative() {
      if (this.type == 'hostingRepresentative' && !this.user.accept) {
        EventBus.$emit('snackbar', {
          text: 'You must accept the terms',
          color: 'error',
        });
        return;
      }

      console.log('[LogIn] Trying to sign up...');

      this.loading = true;

      EventBus.$emit('waiting', true);

      let url = '/api/v1/auth/sign-up-institution-representative';

      const user = {
        email: this.user.email,
        password: this.user.password,
        confirmPassword: this.user.confirmPassword,
        firstname: this.user.firstname,
        lastname: this.user.lastname,
        phone: this.user.phone,
        affiliation: this.user.affiliation,
        institution: this.user.institution,
        institutionText: this.user.institutionText,
      };

      this.axios
        .post(url, user)
        .then(() => {
          console.log('Success');
          this.verify = true;
        })
        .catch(error => {
          console.log(error);
          this.errorSummary = error.response.data;
          if (error.response && error.response.status === 409) {
            EventBus.$emit('snackbar', {
              text: error.response.data,
              color: 'error',
            });
          }
        })
        .finally(() => {
          this.loading = false;
          EventBus.$emit('waiting', false);
        });
    },
    login() {
      if (this.$route.params.applyForm) {
        const basePath = window.location.origin.toString();
        Vue.$keycloak.login({ redirectUri: basePath + '/banner' });
      } else {
        const basePath = window.location.origin.toString();
        Vue.$keycloak.login({ redirectUri: basePath + '/app/dashboard' });
      }
    },
  },
  computed: {
    passwordConfirmationRule() {
      return [this.user.password === this.user.confirmPassword || i18n.t('matching-password-rule')];
    },
  },
};
</script>
